class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedResults = {};
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.trendingAndProductsBlock = this.querySelector('[data-quick-trending-products]');
    this.searchSurface = this.closest('[data-search-surface]');
    this.viewAllContainer = this.searchSurface?.querySelector('[data-search-view-all-container]');
    this.closeStickySearchButton = document.querySelector('.header-search-close');
    this.productToShow = this.dataset.productToShow;
    this.searchDetails = this.querySelector('.search_details');
    this.predictiveSearch = this.querySelector('predictive-search')
    this.isOpen = false;
    this.searchAbortController = null;
    this.searchRequestId = 0;
    this.idNamespaceCounter = 0;

    this.namespaceSearchResultIds(this.trendingAndProductsBlock);
    this.setupEventListeners();
  }
  
  setupEventListeners() {
    const form = this.querySelector('form.search');
    form.addEventListener('submit', this.onFormSubmit.bind(this));
    
    this.input.addEventListener('input', debounce((event) => {
      this.onChange(event);
    }, 500).bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
    document.addEventListener('click', this.onDocClick.bind(this));
    if (this.closeStickySearchButton != null) {
      this.closeStickySearchButton.addEventListener('click', this.onCloseStickySearchClick.bind(this));
    }
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const searchTerm = this.getQuery();
    
    if (!searchTerm.length) {
      this.abortPendingSearch();
      this.close(true);
      this.showTrendingAndProducts();
      return;
    }
    
    this.getSearchResults(searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length) event.preventDefault();
  }

  onFocus() {
    const searchTerm = this.getQuery();
    
    if (!searchTerm.length) return this.showTrendingAndProducts();

    if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement) && !this.searchSurface?.contains(document.activeElement)) {
        this.close();
        // this.hideTrendingAndProducts();
      };
    })
  }
  
  onKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      this.dispatchEvent(new CustomEvent('search:close', { bubbles: true }));
      return;
    }

  }

  abortPendingSearch() {
    this.searchRequestId += 1;
    this.searchAbortController?.abort();
    this.searchAbortController = null;
    this.removeAttribute('loading');
  }
  
  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();

    this.abortPendingSearch();
    const requestId = this.searchRequestId;
    const cachedResult = this.cachedResults[queryKey];

    if (cachedResult) {
      if (this.getQuery() !== searchTerm) return;
      this.renderSearchResults(cachedResult.markup);
      this.updateViewAllLink(searchTerm, cachedResult.total);

      if (Number.isFinite(cachedResult.total)) return;

      const controller = new AbortController();
      this.searchAbortController = controller;
      this.requestSearchResultCount(searchTerm, queryKey, requestId, controller.signal)
        .finally(() => this.releaseSearchController(controller, requestId));
      return;
    }

    this.renderSearchLoadingState();
    this.setLiveRegionLoadingState();

    const params = new URLSearchParams({
      q: searchTerm,
      'resources[type]': 'query,product,collection',
      'resources[options][fields]': 'title,product_type,variants.sku,body,tag',
      'resources[limit]': String(this.productToShow || 5),
      'resources[limit_scope]': 'each',
      section_id: 'predictive-search'
    });
    const url = `${routes.predictive_search_url}?${params.toString()}`;
    
    const controller = new AbortController();
    this.searchAbortController = controller;
    const signal = controller.signal;
    let resolvedTotal;

    const countRequest = this.requestSearchResultCount(searchTerm, queryKey, requestId, signal)
      .then((total) => {
        resolvedTotal = total;
        return total;
      });

    const resultsRequest = fetch(url, { signal })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        if (requestId !== this.searchRequestId || this.getQuery() !== searchTerm) return;

        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;
        this.cachedResults[queryKey] = { markup: resultsMarkup };
        if (Number.isFinite(resolvedTotal)) this.cachedResults[queryKey].total = resolvedTotal;
        this.renderSearchResults(resultsMarkup);
        this.updateViewAllLink(searchTerm, resolvedTotal);
      })
      .catch((error) => {
        if (error.name === 'AbortError' || requestId !== this.searchRequestId || this.getQuery() !== searchTerm) return;
        this.renderSearchError();
      });

    Promise.allSettled([resultsRequest, countRequest])
      .then(() => this.releaseSearchController(controller, requestId));
  }

  requestSearchResultCount(searchTerm, queryKey, requestId, signal) {
    return this.getSearchResultCount(searchTerm, signal)
      .then((total) => {
        if (!Number.isFinite(total) || requestId !== this.searchRequestId || this.getQuery() !== searchTerm) return undefined;

        if (this.cachedResults[queryKey]) {
          this.cachedResults[queryKey].total = total;
          this.updateViewAllLink(searchTerm, total);
        }
        return total;
      })
      .catch((error) => {
        if (error.name === 'AbortError') return undefined;
        return undefined;
      });
  }

  releaseSearchController(controller, requestId) {
    if (requestId === this.searchRequestId && this.searchAbortController === controller) {
      this.searchAbortController = null;
    }
  }

  isDesktopSurface() {
    return this.searchSurface?.dataset.searchSurface === 'desktop';
  }

  renderSearchLoadingState() {
    if (this.isDesktopSurface()) return;

    this.removeAttribute('results');
    this.predictiveSearchResults.innerHTML = `
      <div class="predictive-search__loading-state" aria-hidden="true">
        <svg aria-hidden="true" focusable="false" role="presentation" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
          <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
        </svg>
      </div>`;
    this.hideTrendingAndProducts();
  }

  renderSearchError() {
    const message = 'Não foi possível carregar os resultados. Tente novamente.';

    this.clearViewAllContainer();
    this.removeAttribute('loading');
    this.removeAttribute('results');
    this.predictiveSearchResults.innerHTML = `
      <div class="quickSearchResults quickSearchResultsBlock">
        <div class="container">
          <div class="productEmpty text-left" role="status"><span class="text">${message}</span></div>
        </div>
      </div>`;
    this.setLiveRegionText(message);
    this.hideTrendingAndProducts();
    this.open();
  }

  getSearchResultCount(searchTerm, signal) {
    const params = new URLSearchParams({
      view: 'ajax_quick_search',
      q: searchTerm,
      type: 'product'
    });

    return fetch(`${routes.search_url}?${params.toString()}`, { signal })
      .then((response) => {
        if (!response.ok) throw new Error(response.status);
        return response.json();
      })
      .then((payload) => Number.isFinite(Number(payload.results_total)) ? Number(payload.results_total) : null);
  }

  formatViewAllLabel(searchTerm, total) {
    if (total === 1) return `Mostrar o resultado para “${searchTerm}”`;
    if (Number.isFinite(total)) return `Mostrar todos os ${total} resultados para “${searchTerm}”`;
    return `Mostrar todos os resultados para “${searchTerm}”`;
  }
  
  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
    
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;
    
    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.namespaceSearchResultIds(this.predictiveSearchResults);
    this.syncViewAllContainer();
    this.setAttribute('results', true);
    this.setAttribute('open', true);
    this.setLiveRegionResults();
    this.open();
    this.hideTrendingAndProducts();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  syncViewAllContainer() {
    if (!this.viewAllContainer) return;

    const viewAll = this.predictiveSearchResults?.querySelector('[data-search-view-all]');
    this.viewAllContainer.replaceChildren(...(viewAll ? [viewAll] : []));
  }

  clearViewAllContainer() {
    this.viewAllContainer?.replaceChildren();
  }

  getViewAllLink() {
    return this.viewAllContainer?.querySelector('[data-qs-view-all-link]')
      || this.querySelector('[data-qs-view-all-link]');
  }

  namespaceSearchResultIds(root) {
    if (!root) return;

    const surfaceName = this.searchSurface?.dataset.searchSurface || 'search';
    const referenceAttributes = [
      'for', 'form', 'aria-controls', 'aria-labelledby', 'aria-describedby', 'aria-owns',
      'data-form-id', 'href', 'xlink:href', 'fill', 'stroke', 'clip-path', 'filter', 'mask', 'style'
    ];

    root.querySelectorAll('[id]').forEach((element) => {
      const originalId = element.id;
      if (!originalId) return;

      const namespacedId = `${originalId}-${surfaceName}-${this.idNamespaceCounter++}`;
      const referenceScope = element.closest('.product') || root;

      referenceScope.querySelectorAll('*').forEach((candidate) => {
        referenceAttributes.forEach((attribute) => {
          const value = candidate.getAttribute(attribute);
          if (!value) return;

          if (attribute.startsWith('aria-')) {
            const updatedValue = value.split(/\s+/).map((token) => token === originalId ? namespacedId : token).join(' ');
            candidate.setAttribute(attribute, updatedValue);
          } else if (value === originalId) {
            candidate.setAttribute(attribute, namespacedId);
          } else if (value === `#${originalId}`) {
            candidate.setAttribute(attribute, `#${namespacedId}`);
          } else if (value.includes(`url(#${originalId})`)) {
            candidate.setAttribute(attribute, value.replaceAll(`url(#${originalId})`, `url(#${namespacedId})`));
          }
        });
      });

      element.id = namespacedId;
    });
  }
  
  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - document.querySelector('[class^="header-navigation"]').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }
  
  open() {
    //this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
      this.clearViewAllContainer();
    }
    
    this.removeAttribute('open');
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');

    this.isOpen = false;
  }

  showTrendingAndProducts() {
    if (!this.trendingAndProductsBlock) return 
    this.trendingAndProductsBlock.classList.add('is-show')
    this.trendingAndProductsBlock.classList.remove('hidden');
  }

  hideTrendingAndProducts() {
    if (!this.trendingAndProductsBlock) return 

    this.trendingAndProductsBlock.classList.remove('is-show')
    this.trendingAndProductsBlock.classList.add('hidden');
  }

  onDocClick(e) {
    const isInModal = (this.searchSurface || this).contains(e.target), $target = e.target

    if (!isInModal || $target.closest('.header-search-popup-close') || $target.matches('.header-search-popup-close') || $target.closest('.header-search-close') || $target.matches('.header-search-close')) {
      this.close();
      this.hideTrendingAndProducts();
    }
  }

  onCloseStickySearchClick() {
    this.close();
    this.hideTrendingAndProducts();
    document.querySelector('body').classList.remove('sticky-search-open');
    document.querySelector('body').classList.remove('sticky-search-menu-open');
    this.closest('[class*="section-header-"]')?.classList.remove('sticky-search-menu-open')
    this.closest('[class*="section-header-"]')?.classList.remove('sticky-search-menu-custom-open')
  }

  updateViewAllLink(searchTerm, total) {
    const qsViewAllLink = this.getViewAllLink();
    if (!qsViewAllLink) return;

    qsViewAllLink.href = `${routes.search_url}?q=${encodeURIComponent(searchTerm)}&type=product`;
    qsViewAllLink.textContent = this.formatViewAllLabel(searchTerm, total);
  }
}

customElements.define('predictive-search', PredictiveSearch);
